import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Tag, ChevronDown, ChevronUp } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArticleHeader = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-2xl border-b border-white/[0.04]">
    <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <img
          src="https://customer-assets.emergentagent.com/job_5035be6a-7db4-4e98-9a71-35ab08f9d865/artifacts/z23j60x7_1.svg"
          alt="Disruptiva"
          className="h-8 w-auto"
        />
        <span className="font-space font-semibold text-white text-base tracking-wide hidden sm:block">DISRUPTIVA</span>
      </Link>
      <div className="flex items-center gap-6">
        <Link to="/blog" className="text-[13px] text-white/40 hover:text-white transition-colors font-space tracking-wide">
          <ArrowLeft className="w-3 h-3 inline mr-1" />
          Blog
        </Link>
        <Link to="/#contact" className="btn-primary text-sm py-3 px-6">
          Hablemos
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </nav>
  </header>
);

const FAQItem = ({ faq, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-white/[0.06] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full py-6 flex items-start justify-between text-left gap-4"
        data-testid={`faq-toggle-${index}`}
      >
        <h3 className="font-space font-semibold text-white text-base pr-4">{faq.question}</h3>
        {open ? (
          <ChevronUp className="w-5 h-5 text-purple-400/60 flex-shrink-0 mt-0.5" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/20 flex-shrink-0 mt-0.5" />
        )}
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="pb-6"
        >
          <p className="text-white/40 leading-relaxed text-sm">{faq.answer}</p>
        </motion.div>
      )}
    </div>
  );
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(res.data);
        // Fetch related
        const relRes = await axios.get(`${API}/blog/articles?category=${encodeURIComponent(res.data.category)}&limit=4`);
        setRelated(relRes.data.filter(a => a.slug !== slug).slice(0, 3));
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
        console.error(err);
      }
      setLoading(false);
    };
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <ArticleHeader />
        <div className="pt-32 max-w-3xl mx-auto px-6">
          <div className="animate-pulse space-y-6">
            <div className="h-4 bg-white/5 rounded w-1/4" />
            <div className="h-10 bg-white/5 rounded w-full" />
            <div className="h-10 bg-white/5 rounded w-3/4" />
            <div className="h-4 bg-white/5 rounded w-1/2" />
            <div className="space-y-3 mt-12">
              {[...Array(8)].map((_, i) => <div key={i} className="h-4 bg-white/5 rounded w-full" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-black">
        <ArticleHeader />
        <div className="pt-32 text-center">
          <h1 className="font-space text-4xl text-white mb-4">Artículo no encontrado</h1>
          <Link to="/blog" className="btn-primary">Volver al blog</Link>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const date = new Date(article.published_date).toLocaleDateString("es-ES", {
    year: "numeric", month: "long", day: "numeric"
  });

  return (
    <div className="min-h-screen bg-black">
      <SEOHead
        title={article.meta_title || article.title}
        description={article.meta_description || article.excerpt}
        path={`/blog/${article.slug}`}
        type="article"
        article={article}
        faqs={article.faqs}
      />
      <ArticleHeader />

      <main className="pt-28 pb-20">
        <article className="max-w-3xl mx-auto px-6" data-testid="article-content">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs text-white/25 mb-10" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-white/50 transition-colors">Inicio</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-white/50 transition-colors">Blog</Link>
            <span>/</span>
            <Link to={`/blog?category=${encodeURIComponent(article.category)}`} className="hover:text-white/50 transition-colors">{article.category}</Link>
          </nav>

          {/* Title area */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-xs font-space text-purple-400/70 tracking-wide">{article.category}</span>
            <h1 className="font-space font-bold text-3xl md:text-5xl text-white leading-tight mt-3 mb-6" data-testid="article-title">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-white/30 mb-12 pb-12 border-b border-white/[0.06]">
              <span>{date}</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {article.read_time} min de lectura
              </span>
              <span>{article.author}</span>
            </div>
          </motion.div>

          {/* Article body */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mt-16 pt-8 border-t border-white/[0.06]">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-4 h-4 text-white/20" />
                <span className="text-xs text-white/25 font-space">Tags</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag, i) => (
                  <span key={i} className="text-xs px-3 py-1.5 bg-white/[0.03] border border-white/[0.06] rounded-full text-white/35">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* FAQs */}
          {article.faqs && article.faqs.length > 0 && (
            <section className="mt-16 pt-12 border-t border-white/[0.06]" data-testid="faq-section">
              <h2 className="font-space font-bold text-2xl text-white mb-8">Preguntas frecuentes</h2>
              <div className="glass-card p-6 md:p-8">
                {article.faqs.map((faq, i) => (
                  <FAQItem key={i} faq={faq} index={i} />
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <div className="mt-16 glass-card p-8 md:p-12 text-center" data-testid="article-cta">
            <h3 className="font-space font-bold text-2xl text-white mb-3">
              ¿Necesitas ayuda con esto?
            </h3>
            <p className="text-white/35 mb-8 max-w-md mx-auto">
              En Disruptiva somos especialistas en comunicación estratégica. Hablemos sobre tu situación.
            </p>
            <Link to="/#contact" className="btn-primary">
              Contactar ahora
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Related articles */}
          {related.length > 0 && (
            <section className="mt-20" data-testid="related-articles">
              <h2 className="font-space font-bold text-2xl text-white mb-8">Artículos relacionados</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/blog/${rel.slug}`}
                    className="glass-card p-6 group"
                  >
                    <span className="text-xs font-space text-purple-400/60">{rel.category}</span>
                    <h3 className="font-space font-semibold text-white text-sm mt-2 mb-3 group-hover:text-purple-300 transition-colors leading-snug">
                      {rel.title}
                    </h3>
                    <span className="text-xs text-white/25 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {rel.read_time} min
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-4">
            <img src="https://customer-assets.emergentagent.com/job_5035be6a-7db4-4e98-9a71-35ab08f9d865/artifacts/z23j60x7_1.svg" alt="Disruptiva" className="h-7 w-auto opacity-40" />
            <span className="text-sm text-white/20">© 2026 Disruptiva</span>
          </Link>
          <Link to="/blog" className="text-sm text-white/30 hover:text-white/50 transition-colors">
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Volver al blog
          </Link>
        </div>
      </footer>
    </div>
  );
}
